import { Chain } from '@1delta/chain-registry'
import { multicallRetryUniversal } from '@1delta/providers'
import type { Address } from 'viem'
import { YieldFetcher } from '../../types'
import {
  LINEAR_REWARDS_APR_ABI,
  linearRewardsApr,
  linearRewardsCalls,
} from '../linearRewards'

/**
 * Resupply `sreUSD` — the savings leg of the reUSD CDP.
 *
 * A `LinearRewardsErc4626` over reUSD, i.e. the SAME contract family as Frax's
 * sfrxUSD / sFRAX, so it shares `linearRewards.ts` rather than carrying its own
 * copy of the maths. Verified on-chain 2026-08-05: plain instant 4626,
 * `maxDeposit = uint.max`, no cooldown and no exit fee, 7-day cycle
 * (`REWARDS_CYCLE_LENGTH = 604800`).
 *
 * **What funds it is worth knowing, because it explains the low rate.** sreUSD
 * is NOT paid the whole borrow rate. Resupply pins borrowers at roughly half
 * the greater of {the wrapped market's lend rate, the sfrxUSD "risk-free"
 * rate, a 2 % floor}; what reaches sreUSD is the protocol's revenue share plus
 * the DYNAMIC OFF-PEG component — the amplifier that scales with how far reUSD
 * sits below peg. So the savings rate can sit well under the borrow rate and
 * still be correct: measured 0.74 % APR against a 2.27 % borrow rate.
 *
 * Cross-checked at integration against two independent sources that agree:
 * Resupply's own `Utilities.sreusdRates()` lens (identical formula, 0.7363 %)
 * and DefiLlama's `resupply` SREUSD pool (0.711 %, TVL matching `totalAssets`
 * to the dollar at $22.06M). No API is needed on the happy path — this is
 * computed on-chain and is exact.
 */

const CHAIN_ID = Chain.ETHEREUM_MAINNET

/** sreUSD — `LinearRewardsErc4626` over reUSD. */
const SREUSD: Address = '0x557ab1e003951a73c12d16f0fea8490e39c33c35'

/**
 * Binding keys. The bare symbol is what the savings registry binds
 * `supplyRate` through; the `Name::SYMBOL` form is what the LENDING join
 * (`intrinsicYields[asset.assetGroup]`) looks up.
 *
 * The group key was guessed ahead of the token-list publish (the 3Jane
 * precedent) and is now **CONFIRMED**: sreUSD published as
 * `Savings reUSD::sreUSD`, matching this constant character-for-character
 * (verified 2026-08-08 against the live list, and held there by
 * `test/yields/sreusdMappingLive.test.ts`).
 *
 * Keep that test rather than trusting the string. The join is a bare object
 * lookup, so a one-character drift does not fail — it silently returns 0 %,
 * and only the LENDING side would show it: sreUSD is the COLLATERAL of the
 * live Curve LlamaLend `crvUSD / sreUSD` market (controller `0x4f79fe45…`),
 * which would quietly understate its yield while the savings row kept working
 * off the flat `SREUSD` key.
 */
const SREUSD_KEY = 'SREUSD'
const SREUSD_GROUP_KEY = 'Savings reUSD::sreUSD'

/** sreUSD — Resupply's savings vault. */
export const sreusdSavingsFetcher: YieldFetcher = {
  label: 'SREUSD',
  fetch: async () => {
    const results = (await multicallRetryUniversal({
      chain: CHAIN_ID,
      abi: LINEAR_REWARDS_APR_ABI as any,
      calls: linearRewardsCalls(SREUSD),
      allowFailure: false,
    })) as unknown[]
    const apr = linearRewardsApr(results)
    return { [SREUSD_KEY]: apr, [SREUSD_GROUP_KEY]: apr }
  },
}
